import { FileTextOutlined, LockOutlined } from '@ant-design/icons';
import { ApiClient } from '@api/api-configuration';
import AppAvatar from '@app/components/ui/Avatar';
import envConfig from '@app/enviroment/enviroment';
import { getInitials } from '@app/utils/extractFullnameInitials';
import { useAuthContext } from '@context/auth/useAuthContext';
import { useChangelog } from '@context/changelog/ChangelogContext';
import useSmartErrorHandling from '@hooks/useSmartErrorHandling';
import { useMutation } from '@tanstack/react-query';
import { Button, Dropdown, MenuProps, Space, theme, Typography } from 'antd';
import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ForgotPasswordConfirmModal } from './ForgotPasswordConfirmationModal';

type MenuItem = Required<MenuProps>['items'][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  path?: string,
  icon?: React.ReactNode,
  children?: MenuItem[]
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
    path,
  } as MenuItem;
}

const UserDropdown = () => {
  const { user, logout } = useAuthContext();
  const { openFullChangelog } = useChangelog();
  const { t } = useTranslation();
  const [isForgotModalOpen, setIsForgotModalOpen] = useState(false);
  const [isForgotPasswordProcessImplemented, setIsForgotPasswordProcessImplemented] = useState(false);
  const { handleError } = useSmartErrorHandling();

  const fullnameLetters = getInitials(user.fullname, { fallback: 'NA' });

  const {
    token: { colorBgElevated, borderRadiusLG, boxShadowSecondary },
  } = theme.useToken();

  const contentStyle: React.CSSProperties = {
    backgroundColor: colorBgElevated,
    borderRadius: borderRadiusLG,
    boxShadow: boxShadowSecondary,
  };

  const forgotPasswordMutation = useMutation({
    mutationFn: () =>
      ApiClient.usersApi.users.updateUser(user.userId, {
        requiredActions: ['UPDATE_PASSWORD'],
      }),
    onSuccess: () => {
      setIsForgotPasswordProcessImplemented(true);
    },
    onError: (error) => {
      // Surface the failure to the user instead of only logging it — otherwise
      // the modal silently stays on the confirm view with no feedback.
      handleError(error as Error, {
        customTitle: t('common-error'),
        customMessage: t('layout-forgot-password-error'),
        customType: 'Warning',
        forceShow: true,
      });
    },
  });

  const handleForgotPasswordClick = () => {
    forgotPasswordMutation.mutate();
  };

  const handleCancel = () => {
    setIsForgotModalOpen(false);
  };

  const items: MenuProps['items'] = [
    getItem(
      <div
        className="flex gap-2 items-center justify-start py-1 shadow-none cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          setIsForgotModalOpen(true);
        }}
      >
        <LockOutlined />

        <Typography>{t('layout-change-password')}</Typography>
      </div>,
      '1',
      ''
    ),
    getItem(
      <div
        className="flex gap-2 items-center justify-start py-1 shadow-none cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          openFullChangelog();
        }}
      >
        <FileTextOutlined />

        <Typography>{t('layout-view-changelog')}</Typography>
      </div>,
      '2',
      ''
    ),
  ];

  return (
    <Dropdown
      overlayStyle={{ boxShadow: 'none', border: 'none' }}
      menu={{ items }}
      trigger={['click']}
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      popupRender={(menu) => (
        <div
          style={{
            ...contentStyle,
          }}
        >
          <div className="px-2 py-2">
            <Typography className="cursor-default font-semibold text-center">{user.fullname}</Typography>

            {/* <Typography className="cursor-default font-semibold text-gray-500 text-center">{user.name}</Typography> */}
          </div>

          {menu}

          {/* <div className="mt-2 text-center "><AppVersion theme={'dark'} /></div> */}

          <div className="flex justify-center w-full mt-4">
            <Button className="mb-4" type="default" onClick={logout}>
              {t('common-sign-out')}
            </Button>
          </div>

          <p className="text-[10px] text-gray-500 bg-gray-100 px-4 py-[2px] rounded text-center">
            v{envConfig.getEnvKey('VERSION_TAG') ?? 'unknown'}
          </p>

          <ForgotPasswordConfirmModal
            isOpen={isForgotModalOpen}
            isForgotPasswordProcessImplemented={isForgotPasswordProcessImplemented}
            isSubmitting={forgotPasswordMutation.isPending}
            onCancel={handleCancel}
            onStartReset={handleForgotPasswordClick}
            onSignOut={logout}
          />
        </div>
      )}
    >
      <a onClick={(e) => e.preventDefault()}>
        <Space>
          <AppAvatar isSelected={true} personId={user.userId} fullName={`${user.fullname}`} value={fullnameLetters} />
        </Space>
      </a>
    </Dropdown>
  );
};

export default memo(UserDropdown);
